/* The side cart's coupon box.

   "Apply now" opens a field, and the code goes to the cart through
   /cart/update.js with the drawer rendered in the same request, the way the
   suggestions add a piece. A code Shopify will not apply to this cart is
   taken straight back off, so it never sits in the cart looking applied,
   and the box says why. Applied codes carry a × that removes them.

   No cartUpdate event is published: a code changes no item count, and the
   redraw that event triggers would wipe the message just written. */
class NvCartCoupon extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.nv-coupon__toggle');
    this.form = this.querySelector('.nv-coupon__form');
    this.input = this.querySelector('.nv-coupon__input');
    this.message = this.querySelector('.nv-coupon__msg');
    this.applyButton = this.querySelector('.nv-coupon__apply');

    this.toggle.addEventListener('click', () => this.open(this.form.hidden));
    this.applyButton.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.apply();
    });
    this.querySelectorAll('[data-nv-remove-code]').forEach((button) => {
      button.addEventListener('click', () => this.remove(button.dataset.nvRemoveCode));
    });
  }

  get codes() {
    return (this.dataset.codes || '').split(',').filter(Boolean);
  }

  open(show) {
    this.form.hidden = !show;
    this.toggle.setAttribute('aria-expanded', String(show));
    if (show) this.input.focus();
  }

  say(text, ok = false) {
    this.message.textContent = text;
    this.message.classList.toggle('is-ok', ok);
    this.message.hidden = false;
  }

  busy(on) {
    this.applyButton.disabled = on;
    this.applyButton.setAttribute('aria-busy', String(on));
  }

  async apply() {
    const code = this.input.value.trim();
    if (!code) {
      this.input.focus();
      return;
    }

    this.busy(true);
    try {
      let state = await this.update(code);
      const entered = (state.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());

      if (!entered || !entered.applicable) {
        state = await this.update(this.codes.join(','));
        this.render(state, `"${code}" can't be applied to this cart.`);
        return;
      }
      this.render(state, `"${entered.code}" applied.`, true);
    } catch (err) {
      this.say('Could not apply the code. Please try again.');
      this.busy(false);
    }
  }

  async remove(code) {
    const rest = this.codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
    try {
      this.render(await this.update(rest.join(',')));
    } catch (err) {
      this.say('Could not remove the code. Please try again.');
    }
  }

  async update(discount) {
    const drawer = document.querySelector('cart-drawer');
    const response = await fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount,
        sections: drawer.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error(`cart update ${response.status}`);
    return response.json();
  }

  /* The redraw replaces this element; the message goes on its successor,
     with the field left open after a refusal so the code can be fixed. */
  render(state, text, ok = false) {
    const drawer = document.querySelector('cart-drawer');
    drawer.renderContents(state);
    if (!text) return;

    const fresh = drawer.querySelector('nv-cart-coupon');
    if (!fresh) return;
    if (!ok) fresh.open(true);
    fresh.say(text, ok);
  }
}

if (!customElements.get('nv-cart-coupon')) customElements.define('nv-cart-coupon', NvCartCoupon);
